import { Button, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { HomeIcon, TriangleAlertIcon } from "lucide-react";
import type { FC } from "react";
import type { FallbackProps } from "react-error-boundary";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { IconBox } from "web_myplann/Shared/Components/IconBox";
import { clearPersistedQueryCache } from "web_myplann/Shared/QueryClient";
import { WebViewMessageTypes } from "web_myplann/Utils/WebViewModel";

const PageResources = Resources.Errors.ApplicationError;

export const ApplicationErrorPage: FC<FallbackProps> = ({
	error,
	resetErrorBoundary,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const queryClient = useQueryClient();

	const goHome = () => {
		clearPersistedQueryCache(queryClient);
		window.ReactNativeWebView?.postMessage(
			JSON.stringify({
				type: WebViewMessageTypes.CLEAR_CACHE_REQUEST,
				payload: {},
			}),
		);
		window.location.href = "/";
	};

	return (
		<Stack
			minHeight="100%"
			bgcolor={theme.palette.grey[50]}
			alignItems="center"
			justifyContent="center"
			px={theme.spacingTokens.lg}
			py={4}
		>
			<IconBox bg={theme.palette.red[50]} size={64} mb={theme.spacingTokens.lg}>
				<TriangleAlertIcon
					size={theme.iconSize.lg}
					color={theme.palette.red[600]}
				/>
			</IconBox>

			<Typography variant="h3" textAlign="center" mb={0.75}>
				{t(PageResources.title)}
			</Typography>
			<Typography
				variant="body2"
				textAlign="center"
				mb={3}
				maxWidth={320}
				color={theme.palette.grey[500]}
			>
				{t(PageResources.description)}
			</Typography>

			<Stack width="100%" maxWidth={360} gap={theme.spacingTokens.md}>
				<Button variant="primary" onClick={resetErrorBoundary}>
					{t(PageResources.retry)}
				</Button>
				<Button
					variant="tertiary"
					startIcon={<HomeIcon size={theme.iconSize.sm} />}
					onClick={goHome}
				>
					{t(PageResources.Home)}
				</Button>
			</Stack>

			{error instanceof Error && (
				<Stack
					width="100%"
					maxWidth={360}
					mt={3}
					p={theme.spacingTokens.lg}
					borderRadius={theme.radii.lg}
					bgcolor={theme.palette.grey[100]}
					gap={theme.spacingTokens.xs}
				>
					<Typography variant="overline" color={theme.palette.grey[500]}>
						{t(PageResources.devDetails)}
					</Typography>
					<Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
						{error.name}: {error.message}
					</Typography>
				</Stack>
			)}
		</Stack>
	);
};
